import { useFlag } from '@innovamat/flags';
import { roles, stages } from '@innovamat/radiance-utils';
import { useMemo } from 'react';
import type { Params } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { useUser } from '../providers';

type Props = {
  courseOrder: number | null | undefined;
  classroomId?: string;
};

type ShowDigitalPracticeInformationResult = {
  showDigitalPracticeInformation: boolean;
  showDigitalPracticeNotificationAndLink: boolean;
};

function useShowDigitalPracticeInformation({
  courseOrder,
  classroomId,
}: Props): ShowDigitalPracticeInformationResult {
  const { user, isStudentViewActive } = useUser();

  const { menu, submenu } = useParams<Params>();

  const { value: featureFlagEnabled } = useFlag(
    'showDigitalPracticeInformation',
    false,
    { identifier: '', custom: { classroomId: classroomId || '' } }
  );

  const baseConditionsAreMet = useMemo(() => {
    if (!roles.hasTeacherRole(user?.roles) || isStudentViewActive) return false;
    if (!stages.isSecundaria(courseOrder)) return false;
    return true;
  }, [courseOrder, isStudentViewActive, user?.roles]);

  const conditionsAreMet = useMemo(() => {
    if (!baseConditionsAreMet) return false;
    const shouldShowSlider =
      menu === undefined ||
      menu === 'general' ||
      (menu === 'practice' && submenu === 'home');

    if (!shouldShowSlider) return false;

    return true;
  }, [baseConditionsAreMet, menu, submenu]);

  const showDigitalPracticeInformation = featureFlagEnabled && conditionsAreMet;

  const showDigitalPracticeNotificationAndLink =
    featureFlagEnabled && baseConditionsAreMet;

  return {
    showDigitalPracticeInformation,
    showDigitalPracticeNotificationAndLink,
  };
}

export { useShowDigitalPracticeInformation };
